/**
 * Leitura do corpo de erro da API.
 *
 * Separado de `estados.tsx` porque um arquivo que exporta componentes E funções
 * quebra o fast refresh do Vite — a regra `react-refresh/only-export-components`
 * do eslint pega isso.
 *
 * O contrato de erro do módulo é sempre o mesmo:
 *
 *     { "codigo": "TRANSICAO_BLOQUEADA", "message": "...", "detalhes": {...} }
 *
 * `codigo` é ESTÁVEL e é sobre ele que a tela decide o que fazer. `message` é
 * para o humano ler e pode mudar sem aviso — nunca ramifique sobre ela.
 */

function corpoDoErro(erro: unknown): Record<string, unknown> | null {
  if (erro && typeof erro === 'object' && 'corpo' in erro) {
    const corpo = (erro as { corpo?: unknown }).corpo
    if (corpo && typeof corpo === 'object') return corpo as Record<string, unknown>
  }
  return null
}

/**
 * A mensagem do CORPO da resposta, quando existir.
 *
 * `ErroDeApi` guarda o corpo desserializado em `.corpo`; é lá que mora o
 * `message` em pt-BR que o backend escreveu. O `.message` da exceção é o texto
 * técnico do cliente HTTP, e serve para o console — não para a tela.
 */
export function mensagemDoErro(erro: unknown): string {
  const corpo = corpoDoErro(erro)
  if (corpo && typeof corpo.message === 'string') return corpo.message
  if (erro instanceof Error && erro.message) return erro.message
  return 'Não foi possível completar a operação. Tente novamente em instantes.'
}

/** O `detalhes` do erro de negócio — `camposFaltantes`, `transicoesPermitidas`. */
export function detalhesDoErro(erro: unknown): Record<string, unknown> {
  const corpo = corpoDoErro(erro)
  return (corpo?.detalhes as Record<string, unknown> | undefined) ?? {}
}

/** O `codigo` ESTÁVEL do erro — é sobre ele que a tela decide, nunca sobre o texto. */
export function codigoDoErro(erro: unknown): string | null {
  const corpo = corpoDoErro(erro)
  return typeof corpo?.codigo === 'string' ? corpo.codigo : null
}
